// @flow

import { useState } from 'react';

import { useDispatch } from 'react-redux';

import { removeCartProduct } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';

import { FqdnCell } from 'src/Domain/FqdnCell';
import CartButton from 'src/DomainCreate/components/DomainList/CartButton';

import { RenewPriceWithOption } from './RenewPriceWithOption';
import { addRenewableProductsToCart } from './modules/actions';
import type { ExtendedRenewableProduct, QuotedProduct } from './types';

import styles from './SuggestedRow.scss';

type Props = {
  entry: ExtendedRenewableProduct,
  quotedProduct?: QuotedProduct,
  loadingQuotations: boolean,
  isAddingToCart: boolean,
};

export function SuggestedRow({
  entry,
  isAddingToCart,
  loadingQuotations,
  quotedProduct,
}: Props): React.MixedElement {
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const fqdn = entry.name;

  const handleItemClick: (evt: SyntheticMouseEvent<>) => Promise<void> = async (
    evt: SyntheticMouseEvent<>,
  ) => {
    evt.preventDefault();

    const { product } = entry;

    const productId = product && product.id;

    if (productId) {
      try {
        setLoading(true);
        await dispatch(removeCartProduct(productId));
      } catch (err) {
        dispatch(notifyDanger(__('Unable to remove the product in the cart.')));
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);
        await dispatch(addRenewableProductsToCart(translator, [entry]));
      } catch (err) {
        dispatch(notifyDanger(__('Unable to add the products in the cart.')));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className={styles.suggestion} id={fqdn}>
      <Base flex>
        <FqdnCell
          isValid
          fqdn={fqdn}
          premium={entry.product?.isPremium ? 1 : 0}
          expiresOn={entry.meta_data.expires_at}
        />
      </Base>
      <div className={styles.pricesContainer}>
        {!!quotedProduct && (
          <RenewPriceWithOption quotedProduct={quotedProduct} />
        )}
        <CartButton
          addedToCart={!!entry.product}
          isCartActionnable
          onClick={handleItemClick}
          optimistic={isAddingToCart || loading || loadingQuotations}
        />
      </div>
    </div>
  );
}
